// Kontakter — én samlet, søkbar adressebok for SKADE-operatøren. Slår sammen tre
// kilder til ÉN liste så det er ENKELT å finne noen å sende 1:1 til:
//   * 'unit'  — HV-enheter fra adresseboken (@skade_… roller, fra directory.ts)
//   * 'saved' — GNÅ-kontakter operatøren har lagret manuelt (navn + Matrix-id)
//   * 'seen'  — GNÅ-brukere man har fått melding fra (auto-lært fra innboksen)
// Ren, testbar logikk; forgiving Matrix-id-normalisering så man slipper å skrive
// hele @bruker:domene (deler samme regel som GNÅ-DM-feltet).
import { isMatrixUserId } from './gna-link.js';
/**
 * Tilgivende Matrix-id: godta bare localpart (fyll domenet), og rett vanlig
 * skrivefeil der domenet har kolon i stedet for punktum
 * (@x:matrix:follodefence.com → @x:matrix.follodefence.com).
 */
export function normalizeMatrixId(raw, defaultDomain, sigil = '@') {
    const v = (raw ?? '').trim();
    if (!v)
        return v;
    if (sigil === '@' && isMatrixUserId(v))
        return v;
    const localpart = v.replace(/^[@!]/, '').split(':')[0];
    if (!localpart)
        return v;
    return `${sigil}${localpart}:${defaultDomain}`;
}
/** Kort visningsnavn fra en Matrix-id (localpart uten sigil/domene). */
export function contactShortName(id) {
    return id.replace(/^[@!]/, '').replace(/:.*$/, '');
}
/**
 * Slå sammen kontaktkilder til én liste, deduplisert på id. Rekkefølgen på
 * `lists` er prioritet: tidligere liste vinner navnet (så 'saved' slår 'seen'
 * slår 'unit'). Kilde-flagget beholdes fra vinneren.
 */
export function mergeContacts(...lists) {
    const byId = new Map();
    for (const list of lists) {
        for (const c of list) {
            if (!c.id)
                continue;
            if (!byId.has(c.id))
                byId.set(c.id, { ...c, name: c.name || contactShortName(c.id) });
        }
    }
    return [...byId.values()];
}
// ── Telefonnummer → GNÅ-id (deterministisk) ────────────────────────────────
// GNÅ-kontoer provisjoneres fra telefonnummeret, og localpart-en er
// DETERMINISTISK: «gna» + E.164 uten «+». Autoritativ kilde er
// infra/otp-service.py (normalize_msisdn + matrix_localpart); GNÅ-appen har
// samme speil i lib/matrix/gna_msisdn.dart. Fordi utledningen er ren trenger
// COP-en ingen katalogtjeneste for å adressere en GNÅ-bruker: et nummer i
// søkefeltet ER en adresse.
//
//   +47 91994407  →  @gna4791994407:<server>
const GNA_LOCALPART_PREFIX = 'gna';
/** Landkoder GNÅ støtter (speiler `allowed_cc` i OTP-tjenesten). */
const GNA_LANDKODER = ['47'];
/**
 * Normaliser [raw] til E.164-sifre UTEN «+» («4791994407»), eller null om
 * strengen ikke er et gyldig nummer i et støttet land. Godtar mellomrom,
 * bindestrek og parenteser; «00»-prefiks; og bart 8-sifret norsk nummer.
 */
export function normalizeMsisdnDigits(raw) {
    if (!raw || !raw.trim())
        return null;
    let s = raw.replace(/[\s\-()]/g, '').trim();
    if (s.startsWith('00'))
        s = `+${s.slice(2)}`;
    let digits;
    if (s.startsWith('+')) {
        digits = s.slice(1);
    }
    else if (s.length === 8) {
        digits = `47${s}`; // bart 8-sifret norsk nummer → anta +47
    }
    else {
        return null;
    }
    if (!/^\d+$/.test(digits))
        return null;
    if (!GNA_LANDKODER.some((cc) => digits.startsWith(cc)))
        return null;
    if (digits.length < 8 || digits.length > 15)
        return null;
    return digits;
}
/**
 * Utled GNÅ-Matrix-id-en for et telefonnummer mot [serverHost]
 * (f.eks. «matrix.follodefence.com»), eller null om nummeret er ugyldig.
 */
export function gnaUserIdFromPhone(rawPhone, serverHost) {
    const digits = normalizeMsisdnDigits(rawPhone);
    if (!digits)
        return null;
    return `@${GNA_LOCALPART_PREFIX}${digits}:${serverHost}`;
}
/** Søk på navn, id, undertekst. Tom streng = alt. Sortert: navn A→Å. */
export function searchContacts(all, query, limit = 100) {
    const q = query.trim().toLowerCase();
    const hits = q
        ? all.filter((c) => `${c.name} ${c.id} ${c.sub ?? ''}`.toLowerCase().includes(q))
        : all;
    return [...hits].sort((a, b) => a.name.localeCompare(b.name, 'no')).slice(0, limit);
}
//# sourceMappingURL=contacts.js.map